import { useCallback, useEffect, useMemo, useState } from "react";

import {
  // filterVariants,
  getGiftCardValues,
  getSelectedGiftCardCost,
  getSurchargeDetails,
  formatGiftCardValue,
} from "@root/_utils/PricingUtils";


import { getConversionFee } from "../_utils/PricingUtils";
import {  IAmountChoicesType, IMoneyType, IPricingManagerType } from "../_types/currency";


const usePricingManager = ({
  fulfillmentType,
  settings,
  formData,
  preview,
  bulkOrder,
  inDashboard,
  currency,
  pricingConfig
}: IPricingManagerType) => {

  const [physicalSurcharge, setPhysicalSurcharge] = useState<IMoneyType>({
    value: null,
    formatted: null,
  });

  // const [variants, setVariants] = useState([]);

  const [amountChoices, setAmountChoices] = useState<IAmountChoicesType>({
    values: [],
  });

  const [giftCardCost, setGiftCardCost] = useState<IMoneyType>({
    value: null,
    formatted: null,
  });

  const [conversionSurcharge, setConversionSurcharge] = useState<IMoneyType>({
    value: null,
    formatted: null,
  });

  const [totalCost, setTotalCost] = useState<IMoneyType>({
    value: null,
    formatted: null,
  });


  /**
   * Fetch the gift card product here and remove loading state when done.
   */
  // const fetchGiftCardProduct = (settings: any, attempts = 0) => {

  //   if (preview) {
  //     console.warn("Applying product from settings as we are in preview");
  //     updateAmountChoices(settings, fulfillmentType);
  //     return;
  //   }

  //   fetch(`${window.Shopify?.routes?.root}products/${settings.product.handle}.json`)
  //   .then(res => {
  //     return res.json();
  //   })
  //   .then((res) => {

  //     setVariants(res.product.variants);
  //     updateAmountChoices(res.product.variants, fulfillmentType);
      
  //   })
  //   .catch((err) => {
      
  //     attempts += 1;

  //     if (attempts > 5) {
  //       console.error("Failed to fetch product after 5 attempts", err);
  //       updateAmountChoices(settings?.product?.variants || [], fulfillmentType);
  //       return false;
  //     } else {
  //       console.warn("Couldn't fetch product. Re-attempting", err);
  //       return fetchGiftCardProduct(settings, attempts);
  //     }     

  //   });

  // };

  /**
   * Filter and set gift card products once fetched.
   * @param {*} products
   */
  const updateAmountChoices = useCallback(() => {

    if (!settings || !currency || !physicalSurcharge || !fulfillmentType) {
      return;
    }

    const giftCardValues = getGiftCardValues(
      settings,
      fulfillmentType,
      inDashboard,
      currency,
      physicalSurcharge,
      bulkOrder
    );

    setAmountChoices({ ...amountChoices, values: giftCardValues });

  }, [fulfillmentType, amountChoices, currency, settings, physicalSurcharge]);

  // /**
  //  * Step 1: Load the product when settings are available.
  //  * This will save all available variants to state.
  //  */
  // useEffect(() => {

  //   if (!settings || !Object.keys(settings).length) {
  //     return; // Skip initial mount
  //   }

  //   updateAmountChoices(settings);

  // }, [settings]);



  /**
   * Step 2:
   * Update the amount choices based on the fulfillmentType property.
   * Handle after variants are initially loaded as well.
   *  - Only one of physcial/digital enabled -> Can use all product variants
   *  - Both enabled -> must filter for specific variants based on physical/digital sending selection
   */
  useEffect(() => {

    if (!settings || !pricingConfig || !fulfillmentType) {
      return;
    }

    updateAmountChoices();

  }, [physicalSurcharge, fulfillmentType, pricingConfig, currency, settings]);


  useEffect(() => {

    if (!pricingConfig || !fulfillmentType) {
      return;
    }

    const physicalSurcharge  = getSurchargeDetails(settings, currency, fulfillmentType);

    setPhysicalSurcharge(physicalSurcharge);

  }, [fulfillmentType, currency, settings]);

  /**
   * Step 3:
   * Calculate the gift card cost based on the selected amount.
   * This will also be triggered after step 3, as the amount choices are updated
   * based on the fulfillmentType property.
   */
  useEffect(() => {

    if (!formData || !amountChoices.values.length || !formData.id) {
      return;
    }
    
    const newCost: IMoneyType = getSelectedGiftCardCost(
      amountChoices, 
      pricingConfig, 
      formData, 
      inDashboard, 
      preview,
      currency
    );

    setGiftCardCost(newCost);

  }, [formData?.id, pricingConfig, formData?.properties?._gcp_custom_value, amountChoices?.values, fulfillmentType, physicalSurcharge]);

  /**
   * Step 4:
   * List for changes to the gift card cost and update the conversion surcharge.
   * Calculate Shopify's 2% conversion fee if foreign currency and currencyPolicy is set to shopify_rate.
   */
  useEffect(() => {
    // useCallback(() => {

      if (
        !pricingConfig || 
        !giftCardCost.value || 
        !formData || 
        !currency || 
        !currency.isInternational || 
        !currency.shop || 
        !currency.active || 
        !currency.rate || 
        !formData.id
      ) {
        return;
      }

      const conversionFee: IMoneyType = getConversionFee(
        pricingConfig, 
        formData, 
        currency, 
        giftCardCost, 
        fulfillmentType,
        physicalSurcharge
      );

      setConversionSurcharge(conversionFee);

    // }, [conversionSurcharge, giftCardCost, currency, formData, pricingConfig, physicalSurcharge])

    }, [pricingConfig, formData, currency, giftCardCost, fulfillmentType, physicalSurcharge]);

  /**
   * Keep track of the total gift card cost.
   */
  useEffect(() => {

    if (!giftCardCost.value) {
      return;
    }

    const total = giftCardCost.value + ((conversionSurcharge.value) ? conversionSurcharge.value : 0) + ((physicalSurcharge.value) ? physicalSurcharge.value : 0);

    setTotalCost({
      value: total,
      formatted: formatGiftCardValue(total, currency)
    });

  }, [giftCardCost, conversionSurcharge, physicalSurcharge]);


  /**
   * Scaffold return objects.
   */
  const response = {
    amountChoices,
    giftCardCost,
    conversionSurcharge,
    physicalSurcharge,
    totalCost
  };

  return useMemo(() => response, [response]); //  response, [response]);

}

export default usePricingManager;
